import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import BoltOutlined from "@mui/icons-material/BoltOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import ScheduleOutlined from "@mui/icons-material/ScheduleOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import SupportAgentOutlined from "@mui/icons-material/SupportAgentOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../api/client";
import { getUserFacingApiMessage } from "../utils/getUserFacingApiMessage";
import {
  buildClientReviewFallback,
  isSuggestionComplete,
  normalizeReviewCondition,
  unwrapSuggestResponse,
} from "../utils/automationAiSuggestReview";

const STEPS = { prompt: "prompt", loading: "loading", review: "review" };

const EXAMPLE_DEFS = [
  { id: "bot_distribute", icon: SmartToyOutlined, color: "primary.main" },
  { id: "chatbot_escalation", icon: SupportAgentOutlined, color: "info.main" },
  { id: "ecommerce_email", icon: ShoppingCartOutlined, color: "success.main" },
  { id: "payment_confirmed", icon: PaymentsOutlined, color: "secondary.main" },
  { id: "sla_alert", icon: ScheduleOutlined, color: "warning.main" },
  { id: "lost_followup", icon: EmailOutlined, color: "error.main" },
];

function StepIndicator({ activeStep, t }) {
  const steps = [
    { key: STEPS.prompt, label: t("automationRuleAi.stepDescribe") },
    { key: STEPS.review, label: t("automationRuleAi.stepReview") },
  ];
  const activeIdx = activeStep === STEPS.review ? 1 : 0;

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      {steps.map((s, idx) => {
        const done = idx < activeIdx;
        const active = idx === activeIdx;
        return (
          <React.Fragment key={s.key}>
            {idx > 0 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  bgcolor: done || active ? "primary.main" : "divider",
                  borderRadius: 1,
                  minWidth: 32,
                }}
              />
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 700,
                  bgcolor: done || active ? "primary.main" : "action.selected",
                  color: done || active ? "primary.contrastText" : "text.secondary",
                }}
              >
                {idx + 1}
              </Box>
              <Typography
                variant="caption"
                fontWeight={active ? 700 : 500}
                color={active ? "text.primary" : "text.secondary"}
              >
                {s.label}
              </Typography>
            </Stack>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

function ReviewDetailRow({ label, value, pending, pendingLabel }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 0.75,
        rowGap: 0.25,
        py: 0.375,
      }}
    >
      <Typography variant="body2" color="text.secondary" component="span">
        {label}
      </Typography>
      {pending ? (
        <Chip size="small" variant="outlined" color="warning" label={pendingLabel} sx={{ height: 22 }} />
      ) : (
        <Typography variant="body2" fontWeight={600} component="span">
          {value}
        </Typography>
      )}
    </Box>
  );
}

function ReviewSectionHeader({ icon: Icon, stepLabel, title }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 16, color: "primary.main" }} />
      <Typography variant="caption" color="primary.main" fontWeight={700} letterSpacing={0.4}>
        {stepLabel}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        ·
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
  );
}

function ReviewConditionList({ items, pendingLabel }) {
  return (
    <Stack spacing={0}>
      {items.map((item) => {
        const { label, value } = normalizeReviewCondition(item);
        return (
          <ReviewDetailRow
            key={`${item.key || label}-${value}`}
            label={label}
            value={value}
            pending={item.pending}
            pendingLabel={pendingLabel}
          />
        );
      })}
    </Stack>
  );
}

function ActionReviewItem({ acao, t }) {
  const pendingLabel = t("automationRuleAi.pendingInForm");
  const params = acao.parametros || [];
  const filled = params.filter((p) => p.valueLabel);
  const pending = params.filter((p) => p.pending);

  return (
    <Box
      sx={{
        py: 1,
        px: 1.25,
        borderRadius: 1,
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: filled.length || pending.length ? 0.5 : 0 }}>
        <Box
          sx={{
            minWidth: 22,
            height: 22,
            px: 0.5,
            borderRadius: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {acao.ordem}
        </Box>
        <Typography variant="body2" fontWeight={700} lineHeight={1.35}>
          {acao.nome}
        </Typography>
      </Stack>

      {filled.map((p) => (
        <ReviewDetailRow key={p.key} label={p.label} value={p.valueLabel} />
      ))}
      {pending.map((p) => (
        <ReviewDetailRow key={p.key} label={p.label} value="" pending pendingLabel={pendingLabel} />
      ))}

      {!filled.length && !pending.length && (
        <Typography variant="caption" color="text.secondary">
          {t("automationRuleAi.actionNoParams")}
        </Typography>
      )}
    </Box>
  );
}

export default function AutomationRuleAiSuggestDialog({
  open,
  onClose,
  onApply,
  idObjetoHint = null,
  disabled = false,
}) {
  const { t } = useTranslation("settings");
  const [step, setStep] = useState(STEPS.prompt);
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(STEPS.prompt);
    setPrompt("");
    setResult(null);
    setError(null);
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const handleGenerate = async () => {
    const text = String(prompt || "").trim();
    if (text.length < 10) {
      setError(t("automationRuleAi.promptTooShort"));
      return;
    }

    setError(null);
    setBusy(true);
    setStep(STEPS.loading);

    try {
      const res = await fetchData("/automation-rules/suggest", "POST", {
        prompt: text,
        ...(idObjetoHint ? { id_objeto: Number(idObjetoHint) } : {}),
      });

      const payload = unwrapSuggestResponse(res);
      if (!payload?.suggestion) {
        throw new Error(t("automationRuleAi.invalidResponse"));
      }

      if (!isSuggestionComplete(payload.suggestion)) {
        throw new Error(t("automationRuleAi.incompleteSuggestion"));
      }

      if (!payload.meta?.review?.condicoes?.length && !payload.meta?.review?.acoes?.length) {
        payload.meta = {
          ...(payload.meta || {}),
          review: buildClientReviewFallback(payload.suggestion, payload.meta),
        };
      }

      setResult(payload);
      setStep(STEPS.review);
    } catch (err) {
      setError(getUserFacingApiMessage(err, t("automationRuleAi.generateError")));
      setStep(STEPS.prompt);
    } finally {
      setBusy(false);
    }
  };

  const handleApply = () => {
    if (!result?.suggestion) return;
    onApply?.(result);
    reset();
    onClose?.();
  };

  const suggestion = result?.suggestion;
  const meta = result?.meta || {};
  const review = meta?.review || {};
  const condicoesReview = review?.condicoes || [];
  const acoesReview = review?.acoes || [];
  const canApply = Boolean(suggestion && isSuggestionComplete(suggestion));

  const confidence = meta?.confidence;
  const showConfidenceNotice = confidence === "low" || confidence === "medium";
  const confidenceSeverity = confidence === "low" ? "warning" : "info";

  const contextChips = useMemo(() => {
    const chips = [];
    if (meta.objetoNome) chips.push({ key: "objeto", label: meta.objetoNome });
    return chips;
  }, [meta.objetoNome]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <AutoFixHighOutlined color="primary" sx={{ mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="span" sx={{ display: "block", lineHeight: 1.3 }}>
              {t("automationRuleAi.dialogTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("automationRuleAi.dialogSubtitle")}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <StepIndicator activeStep={step} t={t} />

        {step === STEPS.prompt && (
          <Alert
            severity="info"
            icon={<LightbulbOutlined />}
            sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}
          >
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              {t("automationRuleAi.disclaimerTitle")}
            </Typography>
            <Typography variant="body2">{t("automationRuleAi.disclaimerBody")}</Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === STEPS.prompt && (
          <Stack spacing={2.5}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: "action.hover",
                border: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("automationRuleAi.promptTip")}
              </Typography>
            </Box>

            <TextField
              label={t("automationRuleAi.promptLabel")}
              placeholder={t("automationRuleAi.promptPlaceholder")}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              autoFocus
              disabled={disabled || busy}
              inputProps={{ maxLength: 2000 }}
              helperText={t("automationRuleAi.promptHelper", { count: prompt.length })}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t("automationRuleAi.examplesTitle")}
              </Typography>
              <Grid container spacing={1}>
                {EXAMPLE_DEFS.map(({ id, icon: Icon, color }) => {
                  const title = t(`automationRuleAi.example_${id}_title`);
                  const desc = t(`automationRuleAi.example_${id}_desc`);
                  const text = t(`automationRuleAi.example_${id}_prompt`);
                  const selected = prompt.trim() === text.trim();

                  return (
                    <Grid item xs={12} sm={6} key={id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          borderColor: selected ? "primary.main" : "divider",
                          bgcolor: selected ? "action.selected" : "background.paper",
                        }}
                      >
                        <CardActionArea
                          onClick={() => {
                            setPrompt(text);
                            setError(null);
                          }}
                          disabled={disabled || busy}
                          sx={{ height: "100%" }}
                        >
                          <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                            <Stack direction="row" spacing={1.25} alignItems="flex-start">
                              <Box sx={{ mt: 0.25, color, display: "flex" }}>
                                <Icon fontSize="small" />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {desc}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          </Stack>
        )}

        {step === STEPS.loading && (
          <Box sx={{ py: 4 }}>
            <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
            <Stack spacing={1} alignItems="center">
              <CircularProgress size={36} />
              <Typography variant="subtitle2">{t("automationRuleAi.generatingTitle")}</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={420}>
                {t("automationRuleAi.generating")}
              </Typography>
            </Stack>
          </Box>
        )}

        {step === STEPS.review && suggestion && (
          <Stack spacing={1.5}>
            <Card variant="outlined">
              <Box sx={{ px: 2, py: 1.5, bgcolor: "rgba(25, 118, 210, 0.04)" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="overline" color="text.secondary" letterSpacing={0.6} lineHeight={1.2}>
                      {t("automationRuleAi.summaryName")}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.35} sx={{ mt: 0.25 }}>
                      {suggestion.descricao || t("automationRuleAi.noDescription")}
                    </Typography>
                  </Box>
                  <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap sx={{ flexShrink: 0 }}>
                    {contextChips.map(({ key, label }) => (
                      <Chip key={key} size="small" variant="outlined" label={label} />
                    ))}
                    {showConfidenceNotice && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={confidenceSeverity}
                        icon={<WarningAmberOutlined sx={{ fontSize: "14px !important" }} />}
                        label={t(`automationRuleAi.confidence_${confidence}_short`, {
                          defaultValue: t(`automationRuleAi.confidence_${confidence}`),
                        })}
                      />
                    )}
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={6}
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRight: { sm: "1px solid" },
                    borderBottom: { xs: "1px solid", sm: "none" },
                    borderColor: "divider",
                  }}
                >
                  <ReviewSectionHeader
                    icon={BoltOutlined}
                    stepLabel={t("automationRuleAi.flowWhen")}
                    title={t("automationRuleAi.summaryWhen")}
                  />
                  {meta.eventoNome ? (
                    <Typography variant="body2" fontWeight={600} lineHeight={1.4}>
                      {meta.eventoNome}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t("automationRuleAi.notDefined")}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} sm={6} sx={{ px: 2, py: 1.25 }}>
                  <ReviewSectionHeader
                    icon={FilterAltOutlined}
                    stepLabel={t("automationRuleAi.flowIf")}
                    title={t("automationRuleAi.summaryIf")}
                  />
                  {condicoesReview.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("automationRuleAi.noFilters")}
                    </Typography>
                  ) : (
                    <ReviewConditionList
                      items={condicoesReview}
                      pendingLabel={t("automationRuleAi.pendingInForm")}
                    />
                  )}
                </Grid>
              </Grid>

              <Divider />

              <Box sx={{ px: 2, py: 1.25 }}>
                <ReviewSectionHeader
                  icon={PlayArrowOutlined}
                  stepLabel={t("automationRuleAi.flowThen")}
                  title={t("automationRuleAi.reviewActionsTitle")}
                />
                {acoesReview.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("automationRuleAi.noActions")}
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {acoesReview.map((acao) => (
                      <ActionReviewItem key={`${acao.id}-${acao.ordem}`} acao={acao} t={t} />
                    ))}
                  </Stack>
                )}
              </Box>
            </Card>

            {meta.explanation && (
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "action.hover",
                }}
              >
                <Stack direction="row" spacing={0.75} alignItems="flex-start">
                  <LightbulbOutlined sx={{ fontSize: 16, color: "text.secondary", mt: 0.15 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" display="block">
                      {t("automationRuleAi.explanationTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {meta.explanation}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {(meta.warnings || []).length > 0 && (
              <Alert severity="warning" icon={<WarningAmberOutlined />} sx={{ py: 0.75 }}>
                <Typography variant="caption" fontWeight={700} display="block" gutterBottom>
                  {t("automationRuleAi.warningsTitle")}
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {meta.warnings.map((w) => (
                    <Typography component="li" variant="body2" key={w} sx={{ py: 0.125 }}>
                      {w}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" display="block">
              {t("automationRuleAi.reviewFooter")}
            </Typography>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose} disabled={busy} sx={{ mr: "auto" }}>
          {t("shared.cancel")}
        </Button>
        {step === STEPS.prompt && (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={disabled || busy || prompt.trim().length < 10}
            startIcon={<AutoFixHighOutlined />}
          >
            {t("automationRuleAi.generateButton")}
          </Button>
        )}
        {step === STEPS.review && (
          <>
            <Button onClick={() => setStep(STEPS.prompt)} disabled={busy}>
              {t("automationRuleAi.backToPrompt")}
            </Button>
            <Button variant="contained" color="primary" onClick={handleApply} disabled={busy || !canApply}>
              {t("automationRuleAi.applyButton")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
